import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { WorkspaceSettingsForm } from '@/components/workspace/workspace-settings-form';
import { MembersList } from '@/components/workspace/members-list';
import { NotificationPreferences } from '@/components/settings/notification-preferences';
import { DeepWorkConfig } from '@/components/settings/deep-work-config';
import {
  useWorkspaces,
  useUpdateWorkspace,
  useDeleteWorkspace,
  useWorkspaceMembers,
  useAvailableUsers,
  useAddMember,
  useChangeMemberRole,
  useRemoveMember,
} from '@/hooks/use-workspaces';
import { useWorkspaceStore } from '@/stores/workspace-store';

export const Route = createFileRoute(
  '/_authenticated/workspace/$workspaceId/settings'
)({
  component: WorkspaceSettingsPage,
});

function WorkspaceSettingsPage() {
  const { workspaceId } = Route.useParams();
  const { setActiveWorkspace } = useWorkspaceStore();
  const { data: workspaces } = useWorkspaces();
  const { data: members } = useWorkspaceMembers(workspaceId);
  const { data: availableUsers } = useAvailableUsers(workspaceId);

  const updateWorkspace = useUpdateWorkspace(workspaceId);
  const deleteWorkspace = useDeleteWorkspace(workspaceId);
  const addMember = useAddMember(workspaceId);
  const changeRole = useChangeMemberRole(workspaceId);
  const removeMember = useRemoveMember(workspaceId);

  useEffect(() => {
    if (workspaceId) setActiveWorkspace(workspaceId);
  }, [workspaceId, setActiveWorkspace]);

  const workspace = workspaces?.find((w) => w.id === workspaceId);

  if (!workspace && workspaces) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <h1 className="text-2xl font-bold">403 — Access Denied</h1>
        <p className="mt-2 text-muted-foreground">
          You are not a member of this workspace.
        </p>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div key={i} className="h-48 animate-pulse rounded-xl bg-muted" />
        ))}
      </div>
    );
  }

  const isOwner = workspace.role === 'owner';
  const isAdminOrOwner = workspace.role === 'owner' || workspace.role === 'admin';

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Workspace Settings</h1>
        <p className="text-muted-foreground">{workspace.name}</p>
      </div>

      <WorkspaceSettingsForm
        workspace={{
          id: workspace.id,
          name: workspace.name,
          type: workspace.type,
          activeBatchLimit: workspace.activeBatchLimit,
        }}
        isOwner={isOwner}
        isAdminOrOwner={isAdminOrOwner}
        onUpdate={(data) => updateWorkspace.mutate(data)}
        onDelete={() => deleteWorkspace.mutate()}
      />

      <NotificationPreferences />

      <div>
        <h2 className="mb-3 text-lg font-semibold text-muted-foreground">
          Personal Settings
        </h2>
        <DeepWorkConfig />
      </div>

      {workspace.type !== 'private' && (
        <MembersList
          members={members ?? []}
          availableUsers={
            availableUsers?.map((u) => ({ id: u.id, name: u.name, email: u.email })) ?? []
          }
          isOwner={isOwner}
          isAdminOrOwner={isAdminOrOwner}
          onAdd={(userId, role) => addMember.mutate({ userId, role })}
          onChangeRole={(memberId, role) => changeRole.mutate({ memberId, role })}
          onRemove={(memberId) => removeMember.mutate(memberId)}
          isAdding={addMember.isPending}
        />
      )}
    </div>
  );
}
